import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

// ─── React Plugin (used by AppInsightsContext in the component tree) ──
export const reactPlugin = new ReactPlugin();

// ─── Connection string resolved from environment ────────────────────
// CRA injects REACT_APP_* vars at build time.
//   • `npm start`       → .env.development  (dev App Insights)
//   • `npm run build`   → .env.production   (prod App Insights)
//   • .env.local         → overrides any env (git-ignored)
const connectionString =
  process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING ||
  // Fallback so the app never crashes if the var is missing (sends to dev).
  "InstrumentationKey=dea99455-68c2-414b-a119-d1d5257e1382;" +
  "IngestionEndpoint=https://eastus-8.in.applicationinsights.azure.com/;" +
  "LiveEndpoint=https://eastus.livediagnostics.monitor.azure.com/;" +
  "ApplicationId=682bc678-f25b-47f2-97b8-684cde3f91f3";

// ─── Application Insights instance ──────────────────────────────────
const appInsights = new ApplicationInsights({
  config: {
    connectionString,
    extensions: [reactPlugin],
    enableAutoRouteTracking: true,       // auto-track SPA route changes
    disableFetchTracking: false,         // track outgoing fetch calls
    enableCorsCorrelation: true,         // correlate cross-origin requests
    enableRequestHeaderTracking: true,   // capture request headers for diagnostics
    enableResponseHeaderTracking: true,  // capture response headers for diagnostics
    autoTrackPageVisitTime: true,        // track time spent on pages
    disableExceptionTracking: false,     // capture unhandled exceptions
  },
});

appInsights.loadAppInsights();

// ─── Telemetry helper API ───────────────────────────────────────────

/** Track a custom event (e.g. "TicketSubmitted", "FileAttached"). */
export function trackEvent(name: string, properties?: Record<string, string>) {
  appInsights.trackEvent({ name }, properties);
}

/** Track an exception with optional custom properties. */
export function trackException(
  error: Error,
  severityLevel: SeverityLevel = SeverityLevel.Error,
  properties?: Record<string, string>,
) {
  appInsights.trackException({ exception: error, severityLevel }, properties);
}

/** Track a page view manually (auto-tracking also covers route changes). */
export function trackPageView(name: string, uri?: string) {
  appInsights.trackPageView({ name, uri });
}

/** Track a trace / diagnostic message (replaces console.log / console.warn). */
export function trackTrace(
  message: string,
  severityLevel: SeverityLevel = SeverityLevel.Information,
  properties?: Record<string, string>,
) {
  appInsights.trackTrace({ message, severityLevel }, properties);
}

/** Track a metric (e.g. API response time, file upload size). */
export function trackMetric(name: string, average: number, properties?: Record<string, string>) {
  appInsights.trackMetric({ name, average }, properties);
}

/** Set authenticated user context (call after login). */
export function setAuthenticatedUser(userId: string, accountId?: string) {
  appInsights.setAuthenticatedUserContext(userId, accountId, true);
}

/** Clear user context (call on logout). */
export function clearAuthenticatedUser() {
  appInsights.clearAuthenticatedUserContext();
}

/** Flush any buffered telemetry immediately. */
export function flushTelemetry() {
  appInsights.flush();
}

export { SeverityLevel };
export default appInsights;
